import { ButtonHTMLAttributes, forwardRef, ReactElement } from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'group relative',
    'flex items-center justify-center flex-nowrap rounded-[.8rem] px-spacing-md py-spacing-sm',
    '[&>*]:relative [&>*]:flex [&>*]:overflow-hidden [&>*]:!-my-[0.36em]',
    'transition-colors duration-300 ease-in-out'
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-surface-primary-dark text-text-button-primary-dark hover:bg-text-accent-light-dark'
        ],
        secondary: ['bg-transparent text-text-primary-dark']
      }
    },
    defaultVariants: {
      variant: 'primary'
    }
  }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    endIcon?: ReactElement
  }

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant, endIcon, className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'button'

    return (
      <Component ref={ref} className={cn(buttonVariants({ variant, className }))} {...props}>
        <Slottable>{children}</Slottable>
        {endIcon ? (
          <span className="relative ml-spacing-xxs flex items-center justify-center overflow-hidden">
            <span
              className={cn(
                'flex translate-x-0 pl-spacing-xxs',
                'transition-transform duration-300 ease-out-cubic',
                'group-hover:translate-x-full'
              )}
            >
              {endIcon}
            </span>
            <span
              className={cn(
                'absolute inset-0 -translate-x-full pl-spacing-xxs',
                'transition-transform duration-300 ease-out-cubic',
                'group-hover:translate-x-0'
              )}
            >
              {endIcon}
            </span>
          </span>
        ) : null}
      </Component>
    )
  }
)

export default Button
